import React, { useState } from "react";
import { Card, Progress, List, Button, Modal, InputNumber, Form, Tag, Typography, Row, Col } from "antd";
import { EditOutlined, DollarOutlined, PieChartOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./BudgetOverview.scss";

const { Text, Title } = Typography;

// Mock Data for Budget Overview
const initialCourses = [
  {
    key: '1',
    title: 'Leadership Development',
    totalBudget: 20000,
    usedBudget: 15000,
    startDate: dayjs().subtract(1, 'month'),
    endDate: dayjs().add(2, 'months'),
    breakdown: {
      trainer: 8000,
      environment: 4000,
      certification: 3000,
      logistics: 1000,
    },
  },
  {
    key: '2',
    title: 'Advanced React Training',
    totalBudget: 30000,
    usedBudget: 12000,
    startDate: dayjs().subtract(2, 'months'),
    endDate: dayjs().add(1, 'month'),
    breakdown: {
      trainer: 10000,
      environment: 6000,
      certification: 5000,
      logistics: 1000,
    },
  },
];

const BudgetOverview = () => {
  const [courses, setCourses] = useState(initialCourses);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [form] = Form.useForm();

  const showModal = (course) => {
    form.setFieldsValue({
      ...course.breakdown,
    });
    setEditingCourse(course);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const updatedCourse = {
        ...editingCourse,
        breakdown: { ...values },
        usedBudget: Object.values(values).reduce((sum, val) => sum + val, 0),
      };

      setCourses(courses.map((course) => (course.key === editingCourse.key ? updatedCourse : course)));
      setIsModalVisible(false);
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const totalBudget = courses.reduce((sum, course) => sum + course.totalBudget, 0);
  const totalUsedBudget = courses.reduce((sum, course) => sum + course.usedBudget, 0);

  return (
    <div className="budget-overview">
      {/* Dashboard Header */}
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card>
            <Title level={4}>Total Budget</Title>
            <DollarOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <Text strong style={{ fontSize: '20px' }}> ${totalBudget.toLocaleString()} </Text>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Title level={4}>Total Used Budget</Title>
            <PieChartOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
            <Text strong style={{ fontSize: '20px' }}> ${totalUsedBudget.toLocaleString()} </Text>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Title level={4}>Overall Progress</Title>
            <Progress 
              percent={(totalUsedBudget / totalBudget) * 100} 
              status={totalUsedBudget >= totalBudget ? 'exception' : 'active'}
            />
          </Card>
        </Col>
      </Row>

      {/* Training Plan Cards */}
      <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
        {courses.map((course) => (
          <Col span={12} key={course.key}>
            <Card
              title={`${course.title} Budget Overview`}
              extra={
                <Button icon={<EditOutlined />} onClick={() => showModal(course)}>
                  Edit Budget
                </Button>
              }
              style={{ backgroundColor: "transparent" }}
            >
              <div className="budget-progress-section">
                <Text strong>Total Budget: </Text> ${course.totalBudget.toLocaleString()}
                <Progress
                  percent={(course.usedBudget / course.totalBudget) * 100}
                  status={course.usedBudget >= course.totalBudget ? 'exception' : 'active'}
                  format={() => `$${course.usedBudget.toLocaleString()} / $${course.totalBudget.toLocaleString()}`}
                />
              </div>

              <List
                size="small"
                className="budget-breakdown-list"
                header={<Text strong>Budget Breakdown</Text>}
                dataSource={[
                  { type: "Trainer Costs", allocated: course.breakdown.trainer, used: course.breakdown.trainer },
                  { type: "Environment Costs", allocated: course.breakdown.environment, used: course.breakdown.environment },
                  { type: "Certification Costs", allocated: course.breakdown.certification, used: course.breakdown.certification },
                  { type: "Logistics Costs", allocated: course.breakdown.logistics, used: course.breakdown.logistics },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.type}
                      description={
                        <div className="budget-breakdown-row">
                          <Text>Allocated: <Tag color="blue">${item.allocated.toLocaleString()}</Tag></Text>
                          <Text>Used: <Tag color={item.used > item.allocated ? "red" : "green"}>${item.used.toLocaleString()}</Tag></Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />

              <div className="dates-section">
                <Text>
                  Start Date: <Tag color="blue">{dayjs(course.startDate).format("YYYY-MM-DD")}</Tag>
                </Text>
                <Text>
                  End Date: <Tag color="blue">{dayjs(course.endDate).format("YYYY-MM-DD")}</Tag>
                </Text>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Modal for Editing Budget */}
      <Modal
        title={`Edit Budget for ${editingCourse ? editingCourse.title : ''}`}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Save"
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical" name="budgetEditForm">
          <Form.Item
            name="trainer"
            label="Trainer Costs"
            rules={[{ required: true, message: 'Please input trainer costs' }]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="environment"
            label="Environment Costs"
            rules={[{ required: true, message: 'Please input environment costs' }]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="certification"
            label="Certification Costs"
            rules={[{ required: true, message: 'Please input certification costs' }]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="logistics"
            label="Logistics Costs"
            rules={[{ required: true, message: 'Please input logistics costs' }]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BudgetOverview;
